import ReactDOM from "react-dom";
import { Rectangle, type TooltipProps } from "recharts";
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { cn } from "senf-atomic-design-system/src/util/classNameConstructor";
import { round } from "senf-shared/utils/helpers";

import { type CSSProperties, useMemo } from "react";
import { percentageFormatter } from "./helpers";

// Type definition for chart point data
type ChartPoint = {
  x: string | number;
  y: number;
  count: number;
  percent: number;
  [key: string]: any;
};

export type TooltipConfig = {
  title?:
    | ((props: TooltipProps<ValueType, NameType>) => React.ReactNode)
    | false;
  category?:
    | ((props: TooltipProps<ValueType, NameType>) => React.ReactNode)
    | false;
  value?:
    | ((props: TooltipProps<ValueType, NameType>) => React.ReactNode)
    | false;
  label?:
    | ((props: TooltipProps<ValueType, NameType>) => React.ReactNode)
    | false;
};

export const renderTooltipContent = (
  { payload }: TooltipProps<ValueType, NameType>,
  options?: {
    title?: string;
    className?: string;
    onHovered?: (hovered: boolean) => void;
    vertical?: boolean;
    groupKeys?:
      | Array<{
          key: string;
          label: string;
          color: string;
        }>
      | false;
    config?: TooltipConfig;
  },
) => {
  const { config, className } = options || {};
  return (
    <div
      className={cn(
        "rounded-lg border border-border-subdued p-3 bg-surface-subdued flex flex-col gap-2",
        className,
      )}
      onMouseEnter={() => options?.onHovered?.(true)}
      onMouseLeave={() => options?.onHovered?.(false)}
    >
      {config?.category !== false || config?.title ? (
        <div className="flex flex-col gap-0.5">
          {config?.category !== false ? (
            <span className="text-xs font-bold text-text-subdued uppercase tracking-[0.025em]">
              {config?.category?.(payload?.[0]?.payload) ||
                `Response: ${payload?.[0]?.payload?.name}`}
            </span>
          ) : null}
          {config?.title ? (
            <span className="text-base font-bold">
              {config?.title?.(payload?.[0]?.payload)}
            </span>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn("flex gap-x-10 gap-y-3", options?.vertical && "flex-col")}
      >
        {payload?.map((e) => {
          const dataKey = e.dataKey;
          const dataPoint = e.payload || {};

          const percentKey =
            `${dataKey}Percent` in e.payload
              ? e.payload[`${dataKey}Percent`]
              : dataPoint.percent;

          let displayValue = dataPoint.value;
          if (dataPoint.y != null) {
            displayValue = dataPoint.y;
          }
          if (percentKey != null) {
            displayValue = percentageFormatter(percentKey);
          }

          const displayName = [
            options?.groupKeys
              ? options.groupKeys.find((group) => group.key === e.name)
                  ?.label ||
                e.name ||
                e.payload.name
              : undefined,

            e.value != null ? `(${e.value})` : undefined,
          ]
            .filter(Boolean)
            .join(" ");

          const color =
            (e.color !== "transparent" && e.color) || dataPoint.fill || e.fill;
          return (
            <div
              key={`tooltip-${displayName}-${e.name}`}
              className="relative flex flex-col gap-1 whitespace-nowrap"
            >
              <div className="relative flex gap-1">
                {color && (
                  <div
                    className="w-1 h-full rounded-sm absolute start-0"
                    style={{ backgroundColor: color }}
                  />
                )}
                <div className={cn("flex flex-col -my-0.5", color && "ms-3")}>
                  <span className="text-2xl font-bold">
                    {config?.value?.(e) || displayValue}
                  </span>
                  <span className="flex items-center gap-1 text-sm font-bold text-text-subdued">
                    {config?.label?.(
                      e,
                      options.groupKeys.find((group) => group.key === e.name),
                    ) || String(displayName)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const CustomCursor = ({
  pointerEvents,
  width,
  height,
  className,
  direction,
  groupKeys,
  containerRef,
  payload,
  cursor,
  config,
  ...props
}: any) => {
  const isHorizontal = direction === "horizontal";
  const extension = 24;
  const x =
    props.points?.[0]?.x || (isHorizontal ? props.x : props.x + width / 2);
  const y = props.points
    ? 0 - 14
    : isHorizontal
      ? props.y + height / 2
      : props.y - extension;
  const { width: containerWidth, height: containerHeight } =
    containerRef.current?.getBoundingClientRect() || {};
  const thinLine = !groupKeys || new Set(groupKeys.map(e => e.stackId)).size === 1 || props.points;
  const thickness = thinLine ? 1 : isHorizontal ? height : width;
  const tooltipStyle: CSSProperties = {
    top: 0,
  };
  let translateX = "0%";
  let translateY = `min(max(calc(${y}px - 50%), 0px), calc(${containerHeight}px - 100%))`;
  if (isHorizontal) {
    tooltipStyle.insetInlineEnd = 0;
    console.log(props, props.x, x, containerWidth, width);
  } else {
    tooltipStyle.insetInlineStart = 0;
    translateX = `min(max(calc(${x}px - 50%), 0px), calc(${containerWidth}px - 100%))`;
    translateY = "-100%";
  }
  tooltipStyle.transform = `translate(${translateX}, ${translateY})`;
  return (
    <>
      {ReactDOM.createPortal(
        <span
          className="absolute z-50 duration-200 ease-out"
          style={tooltipStyle}
        >
          {renderTooltipContent(
            { payload },
            { vertical: isHorizontal, groupKeys, config },
          )}
        </span>,
        containerRef.current,
      )}
      <Rectangle
        x={x - (!isHorizontal ? thickness / 2 : 0) - (thinLine ? 0.75 : 0)}
        y={y - (isHorizontal ? thickness / 2 : 0)}
        pointerEvents={pointerEvents}
        width={isHorizontal ? width + extension : thickness}
        height={isHorizontal ? thickness : height + extension}
        fill={thinLine ? "var(--border-subdued)" : "var(--background-hovered)"}
        className={className}
      />
    </>
  );
};

const getLabel = ({
  name,
  value,
  title,
  hide,
  onHovered,
  groupKeys,
}: {
  name: string;
  value: number;
  title: string;
  hide: boolean;
  onHovered?: (hovered: boolean) => void;
  groupKeys?:
    | Array<{
        key: string;
        label: string;
        color: string;
      }>
    | false;
}) =>
  renderTooltipContent(
    {
      payload: [
        {
          payload: {
            x: name,
            y: value,
            count: value,
            percent: null,
            name,
          },
        },
      ],
    },
    {
      title: title,
      className: cn(
        "border-none bg-transparent duration-75 -m-3 pr-10 py-6",
        hide && "opacity-0",
      ),
      onHovered,
      groupKeys,
    },
  );

export const AverageValueLabel = ({
  data,
  average,
  median,
  total,
  onHoverAverage,
  onHoverMedian,
  onHoverTotal,
  hide = false,
  groupKeys = false,
}: {
  data: ChartPoint[];
  average?: string;
  median?: string;
  total?: string;
  onHoverAverage?: (hovered: boolean) => void;
  onHoverMedian?: (hovered: boolean) => void;
  onHoverTotal?: (hovered: boolean) => void;
  hide?: boolean;
  groupKeys?:
    | Array<{
        key: string;
        label: string;
        color: string;
      }>
    | false;
}) => {
  // Calculate average based on y or value property

  const totalValue = useMemo(
    () =>
      data.reduce((acc, curr) => {
        const val = "y" in curr ? curr.y : curr.value;
        return acc + val;
      }, 0),
    [data],
  );

  const averageValue = useMemo(
    () => round(totalValue / data.length, 1),
    [totalValue, data],
  );

  // Get values array for median calculation
  const medianValue = useMemo(() => {
    const values = data.map((item) => ("y" in item ? item.y : item.value));
    return round(
      values.sort((a, b) => a - b)[Math.floor(values.length / 2)],
      1,
    );
  }, [data]);

  return (
    <div className="flex">
      {total &&
        getLabel({
          name: total,
          value: totalValue,
          title: "Total",
          hide,
          onHovered: onHoverTotal,
          groupKeys,
        })}
      {average &&
        getLabel({
          name: average,
          value: averageValue,
          title: "Average",
          hide,
          onHovered: onHoverAverage,
          groupKeys,
        })}
      {median &&
        getLabel({
          name: median,
          value: medianValue,
          title: "Median",
          hide,
          onHovered: onHoverMedian,
          groupKeys,
        })}
    </div>
  );
};
